import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useI18n } from "../i18n/LanguageContext";
import {
  createConfirmationChallenge,
  createConfirmationId,
  createConfirmationReceipt,
  cancelConfirmationReceipt,
  downloadConfirmationCertificate,
  evaluateConfirmationRisk,
  getConfirmationPreferences,
  getConfirmationRollout,
  recordConfirmationEvent,
  submitConfirmationFeedback,
  verifyConfirmationSecurity,
} from "../services/actionConfirmationApi";
import {
  completeOfflineConfirmation,
  discardOfflineConfirmation,
  offlineConfirmationsForCurrentPath,
  reserveOfflineConfirmation,
} from "../services/confirmationOfflineQueue";
import PremiumParticipationConfirmation from "./PremiumParticipationConfirmation";

const PremiumActionConfirmationContext = createContext(null);
const SECURITY_LEVELS = new Set(["light", "standard", "reinforced", "critical"]);
const SECURITY_ORDER = Object.freeze({ light: 0, standard: 1, reinforced: 2, critical: 3 });
const DEFAULT_PREFERENCES = Object.freeze({ animation_intensity: "full", contrast_mode: "standard", control_size: "standard", screen_reader_detail: true, simplify_familiar_actions: false, email_receipts_by_default: false, high_value_threshold: "500.00", preferred_authenticator: "auto" });

const UNIVERSE_PRESETS = Object.freeze({
  market: { variant: "market", brandLabel: "MARKETPLACE PRIVÉE", category: "Marketplace", symbol: "◈" },
  auction: { variant: "auction", brandLabel: "ENCHÈRES SIGNATURE", category: "Enchères", symbol: "♢" },
  flash: { variant: "flash", brandLabel: "VENTE FLASH", category: "Vente flash", symbol: "ϟ" },
  sport: { variant: "sport", brandLabel: "SPORT & LOISIRS", category: "Sport & loisirs", symbol: "✦" },
  sortie: { variant: "sortie", brandLabel: "SORTIE PRIVÉE", category: "Sortie", symbol: "◐" },
  crowdfunding: { variant: "crowdfunding", brandLabel: "CROWDFUNDING PRIVÉ", category: "Crowdfunding", symbol: "◇" },
  voyage: { variant: "voyage", brandLabel: "VOYAGE PRIVÉ", category: "Voyage", symbol: "✧" },
  babysitting: { variant: "babysitting", brandLabel: "BABYSITTING PROTÉGÉ", category: "Babysitting", symbol: "♧" },
  animaux: { variant: "animaux", brandLabel: "ANIMAUX PROTÉGÉS", category: "Animaux", symbol: "♡" },
  immobilier: { variant: "immobilier", brandLabel: "IMMOBILIER SIGNATURE", category: "Immobilier & Séjours", symbol: "⌂" },
  emploi: { variant: "emploi", brandLabel: "CARRIÈRE PRIVÉE", category: "Emploi", symbol: "◆" },
  services: { variant: "services", brandLabel: "SERVICES SÉCURISÉS", category: "Services", symbol: "✦" },
  forum: { variant: "forum", brandLabel: "COMMUNAUTÉ PRIVÉE", category: "Forum", symbol: "◌" },
  amis: { variant: "amis", brandLabel: "CERCLE PRIVÉ", category: "Amis", symbol: "◇" },
  donner: { variant: "donner", brandLabel: "ENTRAIDE PRIVÉE", category: "Donner & entraide", symbol: "✺" },
  premium: { variant: "premium", brandLabel: "PREMIUM SIGNATURE", category: "Abonnement Eternua", symbol: "♕" },
});

function actionKey(value) {
  return String(value || "action-eternua")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 96) || "action-eternua";
}

export function inferConfirmationSecurity(configuration = {}) {
  if (SECURITY_LEVELS.has(configuration.securityLevel)) return configuration.securityLevel;
  const text = [configuration.actionKey, configuration.actionLabel, configuration.heading, configuration.confirmLabel]
    .join(" ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (configuration.paymentFollows && !configuration.demo) return "critical";
  if (configuration.paid || /(payer|paiement|acheter|achat|enchere|abonnement|contribution|don)/.test(text)) return "reinforced";
  if (/(supprimer|retirer|bloquer|annuler|rejeter|refuser|archiver|rembourser)/.test(text)) return "reinforced";
  if (/(publier|publication|commentaire|reponse|envoyer|partager)/.test(text)) return "light";
  return "standard";
}

export function premiumActionConfiguration({
  universe,
  heading,
  title,
  description,
  actionLabel = "Action demandée",
  confirmLabel = "Confirmer l’action",
  consentLabel = "Je confirme cette action et ses conséquences.",
  consentDetail = "L’action ne sera exécutée qu’après cette confirmation.",
  date = "Maintenant",
  location = "Sur Eternua",
  organizer = "Votre compte",
  priceLabel = "Aucun débit",
  feeLabel = "Frais immédiats",
  feeValue = "Aucun",
  paymentDetail = "Aucun paiement déclenché par cette confirmation",
  totalLabel = "Impact financier immédiat",
  assurances,
  footnote = "Confirmation sécurisée · Action enregistrée uniquement après votre accord",
  securityLevel = "auto",
  ...overrides
}) {
  const preset = UNIVERSE_PRESETS[universe] || UNIVERSE_PRESETS.premium;
  const configuration = {
    ...preset,
    universe: universe || "premium",
    heading: heading || "Confirmer votre action",
    title: title || actionLabel,
    description: description || "Relisez les informations essentielles avant de poursuivre.",
    experienceEyebrow: "ACTION À CONFIRMER",
    summaryLabel: "Récapitulatif de l’action",
    date,
    location,
    organizer,
    priceLabel,
    unitLabel: actionLabel,
    actionLabel,
    actionKey: overrides.actionKey || actionKey(actionLabel),
    feeLabel,
    feeValue,
    paymentDetail,
    totalLabel,
    paid: false,
    paymentFollows: false,
    confirmLabel,
    consentLabel,
    consentDetail,
    securityLevel,
    assurances: assurances || [
      [preset.symbol, "Action identifiée", actionLabel],
      ["✓", "Contrôle final", "Aucune action avant votre accord explicite"],
      ["◈", "Traçabilité Eternua", "Confirmation protégée et contextualisée"],
    ],
    footnote,
    ...overrides,
  };
  configuration.securityLevel = configuration.securityLevel === "auto"
    ? inferConfirmationSecurity(configuration)
    : configuration.securityLevel;
  return configuration;
}

function apiErrorMessage(error, t) {
  const status = Number(error?.response?.status || 0);
  const data = error?.response?.data;
  if (status === 401) return t("confirmation.auth_required", "Connectez-vous pour utiliser cette validation sécurisée.");
  if (status === 403) return t("confirmation.forbidden", "Votre compte n’est pas autorisé à confirmer cette action.");
  if (status === 429) return t("confirmation.rate_limited", "Trop de tentatives. Patientez un instant avant de réessayer.");
  if (status === 400 && data?.confirmation_code) return t("confirmation.error_code", "Le code de confirmation est incorrect.");
  if (status === 400 && data?.password) return t("confirmation.error_password", "Le mot de passe du compte est incorrect.");
  if (status === 400) return t("confirmation.error_validation", "Vérifiez les informations saisies avant de réessayer.");
  if (!status && error?.message) return String(error.message);
  return t("confirmation.error_generic", "Cette action n’a pas pu être confirmée. Réessayez dans un instant.");
}

function smartUndoSeconds(configuration, result) {
  if (Number.isFinite(Number(configuration.undoWindowSeconds))) return Math.max(0, Math.min(900, Number(configuration.undoWindowSeconds)));
  if (!(typeof configuration.undoAction === "function" || typeof result?.undo === "function")) return 0;
  const text = `${configuration.actionKey || ""} ${configuration.actionLabel || ""}`.toLowerCase();
  if (/(reservation|booking|réservation)/.test(text)) return 300;
  if (/(publish|publier|publication|create|créer)/.test(text)) return 30;
  return 30;
}

function ReceiptToast({ receipt, onClose, onUndo }) {
  useSourceLanguage();
  const { t } = useI18n();
  const initialSeconds = receipt.undo_deadline ? Math.max(0, Math.ceil((new Date(receipt.undo_deadline).getTime() - Date.now()) / 1000)) : 0;
  const [remaining, setRemaining] = useState(initialSeconds);
  const [undoBusy, setUndoBusy] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  useEffect(() => {
    recordConfirmationEvent({
      event_type: "receipt_viewed",
      session_id: receipt.session_id,
      universe: receipt.universe,
      action_key: receipt.action_key,
      reference: receipt.reference,
      security_level: receipt.security_level,
      receipt_id: receipt.public_id,
    });
  }, [receipt]);
  useEffect(() => {
    if (!remaining) return undefined;
    const timer = window.setInterval(() => setRemaining(Math.max(0, Math.ceil((new Date(receipt.undo_deadline).getTime() - Date.now()) / 1000))), 500);
    return () => window.clearInterval(timer);
  }, [receipt.undo_deadline, remaining]);
  const undo = async () => {
    if (!onUndo || !remaining || undoBusy) return;
    setUndoBusy(true);
    try { await onUndo(); } finally { setUndoBusy(false); }
  };
  const sendFeedback = async (trustScore, accessibilityIssue = false) => {
    if (feedbackSent) return;
    try {
      await submitConfirmationFeedback({ receipt_id: receipt.public_id, trust_score: trustScore, understood: trustScore >= 3, accessibility_issue: accessibilityIssue });
      setFeedbackSent(true);
    } catch (_error) { /* Le reçu reste prioritaire si la mesure facultative échoue. */ }
  };
  return (
    <aside className="premium-confirmation-toast" role="status" aria-live="polite">
      <span aria-hidden="true">✓</span>
      <div><small>{<LocalizedText source={t("confirmation.receipt_eyebrow", "CONFIRMATION ENREGISTRÉE")} />}</small><strong>{<LocalizedText source={t("confirmation.receipt_title", "Votre reçu Eternua")} />}</strong><code>{receipt.public_id}</code></div>
      <div className="premium-confirmation-toast__actions"><Link to={`/mes-eternua/demarches?confirmation=${encodeURIComponent(receipt.public_id)}`}>{<LocalizedText source={t("confirmation.history", "Voir dans Mes démarches")} />}</Link><Link to={`/mes-eternua/support?confirmation=${encodeURIComponent(receipt.public_id)}`}><LocalizedText source={"Contester ou demander un remboursement"} /></Link><button type="button" onClick={() => downloadConfirmationCertificate(receipt.public_id)}>{<LocalizedText source={t("confirmation.pdf", "PDF certifié")} />}</button>{onUndo && remaining > 0 ? <button type="button" className="is-undo" onClick={undo} disabled={undoBusy}>{undoBusy ? <LocalizedText source={t("confirmation.undoing", "Annulation…")} /> : `${t("confirmation.undo", "Annuler pendant")} ${remaining} s`}</button> : null}</div>
      <div className="premium-confirmation-toast__feedback">{feedbackSent ? <small><LocalizedText source={"Merci, votre retour est enregistré."} /></small> : <><small><LocalizedText source={"Cette confirmation vous inspire confiance ?"} /></small><span>{[1, 2, 3, 4, 5].map((score) => <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]} type="button" key={score} aria-label={sourceAttribute(`${score} sur 5`, "__ET_VALUE_0__ sur 5")} onClick={() => sendFeedback(score)}>{<LocalizedText source={score} />}</LocalizedElement>)}</span><button type="button" onClick={() => sendFeedback(2, true)}><LocalizedText source={"Signaler un problème d’accessibilité"} /></button></>}</div>
      <button type="button" onClick={onClose} aria-label={t("confirmation.close", "Fermer")}>×</button>
    </aside>
  );
}

export function PremiumActionConfirmationProvider({ children }) {
  useSourceLanguage();
  const { lang, t } = useI18n();
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [undoHandler, setUndoHandler] = useState(null);
  const [recoverableDrafts, setRecoverableDrafts] = useState(() => offlineConfirmationsForCurrentPath());
  const completedActionRef = useRef(null);
  const confirmingRef = useRef(false);

  useEffect(() => {
    let active = true;
    if (typeof getConfirmationPreferences !== "function") return () => { active = false; };
    getConfirmationPreferences().then((value) => { if (active) setPreferences({ ...DEFAULT_PREFERENCES, ...value }); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const refresh = () => setRecoverableDrafts(offlineConfirmationsForCurrentPath());
    window.addEventListener("online", refresh);
    window.addEventListener("storage", refresh);
    return () => { window.removeEventListener("online", refresh); window.removeEventListener("storage", refresh); };
  }, []);

  const eventPayload = useCallback((current, eventType, extra = {}) => ({
    event_type: eventType,
    session_id: current.sessionId,
    universe: current.configuration.universe || current.configuration.variant || "premium",
    action_key: current.configuration.actionKey || actionKey(current.configuration.actionLabel),
    reference: current.configuration.reference || current.sessionId,
    security_level: current.configuration.securityLevel || "standard",
    duration_ms: Math.max(0, Math.round(performance.now() - current.openedAt)),
    risk_score: current.configuration.risk?.score || 0,
    rollout_experience: current.configuration.confirmationExperience || "",
    step_key: eventType,
    ...extra,
  }), []);

  const requestConfirmation = useCallback((rawConfiguration, action) => {
    if (typeof action !== "function") return;
    if (confirmingRef.current) return;
    completedActionRef.current = null;
    const configuration = {
      ...rawConfiguration,
      universe: rawConfiguration.universe || rawConfiguration.variant || "premium",
      actionKey: rawConfiguration.actionKey || actionKey(rawConfiguration.actionLabel || rawConfiguration.unitLabel),
      securityLevel: rawConfiguration.securityLevel === "auto" || !SECURITY_LEVELS.has(rawConfiguration.securityLevel)
        ? inferConfirmationSecurity(rawConfiguration)
        : rawConfiguration.securityLevel,
    };
    const offlineDraft = reserveOfflineConfirmation(configuration, () => createConfirmationId("confirmation-session"));
    const sessionId = offlineDraft.idempotency_key;
    setRecoverableDrafts(offlineConfirmationsForCurrentPath());
    if (preferences.simplify_familiar_actions && rawConfiguration.familiarAction && SECURITY_ORDER[configuration.securityLevel] <= SECURITY_ORDER.standard) {
      configuration.securityLevel = "light";
    }
    let next;
    setError("");
    setBusy(false);
    setReceipt(null);
    setUndoHandler(null);
    const riskPayload = { universe: configuration.universe, action_key: configuration.actionKey, reference: configuration.reference || sessionId, amount: configuration.amount ?? null };
    const riskRequest = typeof evaluateConfirmationRisk === "function" ? evaluateConfirmationRisk(riskPayload) : Promise.resolve({ score: 0, reasons: [], recommended_security: configuration.securityLevel });
    const rolloutRequest = typeof getConfirmationRollout === "function" ? getConfirmationRollout(configuration.universe) : Promise.resolve({ experience: "premium", premium_enabled: true });
    const riskPromise = Promise.all([
      riskRequest.catch(() => ({ score: 0, reasons: [], recommended_security: configuration.securityLevel })),
      rolloutRequest.catch(() => ({ experience: "stable", premium_enabled: false, reason: "rollout_unavailable" })),
    ]).then(async ([risk, rollout]) => {
      const recommended = SECURITY_LEVELS.has(risk.recommended_security) ? risk.recommended_security : configuration.securityLevel;
      const finalLevel = SECURITY_ORDER[recommended] > SECURITY_ORDER[configuration.securityLevel] ? recommended : configuration.securityLevel;
      const updatedConfiguration = { ...configuration, securityLevel: finalLevel, risk, confirmationExperience: rollout.experience || "stable", rollout };
      if (!["reinforced", "critical"].includes(finalLevel)) {
        const resolved = { configuration: updatedConfiguration, challenge: null, securityLoading: false, securityError: "" };
        setPending((current) => current?.sessionId === sessionId ? { ...current, ...resolved } : current);
        return resolved;
      }
      setPending((current) => current?.sessionId === sessionId ? { ...current, configuration: updatedConfiguration, securityLoading: true } : current);
      try {
        const challenge = await createConfirmationChallenge({ ...riskPayload, security_level: finalLevel });
        const serverLevel = challenge.security_level || finalLevel;
        const resolved = { configuration: { ...updatedConfiguration, securityLevel: serverLevel, risk: challenge.risk || risk }, challenge, securityLoading: false, securityError: "" };
        setPending((current) => current?.sessionId === sessionId ? { ...current, ...resolved } : current);
        return resolved;
      } catch (challengeError) {
        const resolved = { configuration: updatedConfiguration, challenge: null, securityLoading: false, securityError: apiErrorMessage(challengeError, t) };
        setPending((current) => current?.sessionId === sessionId ? { ...current, ...resolved } : current);
        return resolved;
      }
    });
    next = { configuration, action, sessionId, openedAt: performance.now(), challenge: null, securityLoading: ["reinforced", "critical"].includes(configuration.securityLevel), securityError: "", riskPromise };
    setPending(next);
    recordConfirmationEvent(eventPayload(next, "opened"));
  }, [eventPayload, preferences.simplify_familiar_actions, t]);

  const close = useCallback((reason = "button") => {
    if (busy) return;
    setPending((current) => {
      if (current) {
        recordConfirmationEvent(eventPayload(current, "cancelled", { reason }));
        completeOfflineConfirmation(current.sessionId);
        setRecoverableDrafts(offlineConfirmationsForCurrentPath());
      }
      return null;
    });
    setError("");
  }, [busy, eventPayload]);

  const consentChange = useCallback((accepted) => {
    if (accepted && pending) recordConfirmationEvent(eventPayload(pending, "consented"));
  }, [eventPayload, pending]);

  const confirm = useCallback(async ({ confirmationCode = "", password = "", totpCode = "", authenticationMethod = "display_code", verificationToken: suppliedVerificationToken = "", emailRequested = false } = {}) => {
    if (!pending || busy || confirmingRef.current) return;
    confirmingRef.current = true;
    setBusy(true);
    setError("");
    let verificationToken = suppliedVerificationToken;
    try {
      const riskResolution = await pending.riskPromise;
      const activePending = { ...pending, ...riskResolution };
      const configuration = activePending.configuration;
      if (!completedActionRef.current && ["reinforced", "critical"].includes(configuration.securityLevel) && !verificationToken) {
        if (!activePending.challenge?.challenge_token) throw new Error(activePending.securityError || "La validation renforcée est indisponible.");
        try {
          const verification = await verifyConfirmationSecurity({
            challenge_token: activePending.challenge.challenge_token,
            confirmation_code: confirmationCode,
            totp_code: totpCode,
            authentication_method: authenticationMethod,
            password,
          });
          verificationToken = verification.verification_token;
        } catch (securityError) {
          recordConfirmationEvent(eventPayload(pending, "security_failed"));
          throw securityError;
        }
      }
      if (!completedActionRef.current) {
        recordConfirmationEvent(eventPayload(pending, "confirm_started"));
        const result = await pending.action();
        if (result === false) return;
        completedActionRef.current = { result, verificationToken };
      }
      const completed = completedActionRef.current;
      const { result } = completed;
      let createdReceipt = null;
      const undoSeconds = smartUndoSeconds(configuration, result);
      try {
        if (!completed.receiptPayload) completed.receiptPayload = {
          universe: configuration.universe,
          action_key: configuration.actionKey,
          action_label: configuration.actionLabel || configuration.unitLabel || configuration.confirmLabel,
          title: configuration.title,
          reference: configuration.reference || pending.sessionId,
          amount: configuration.amount ?? null,
          currency: configuration.currency || "EUR",
          security_level: configuration.securityLevel,
          verification_token: completed.verificationToken,
          locale: lang,
          email_requested: emailRequested,
          undo_window_seconds: undoSeconds,
          idempotency_key: pending.sessionId,
          metadata: {
            category: configuration.category,
            date: configuration.date,
            location: configuration.location,
            organizer: configuration.organizer,
            price_label: configuration.priceLabel,
            total_label: configuration.totalLabel,
            rollout_experience: configuration.confirmationExperience || "premium",
            rollout_reason: configuration.rollout?.reason || "default",
          },
        };
        createdReceipt = await createConfirmationReceipt(completed.receiptPayload);
        if (!createdReceipt?.public_id) throw new Error("receipt_unavailable");
      } catch (_receiptError) {
        setPending((current) => current?.sessionId === pending.sessionId ? { ...current, receiptPending: true } : current);
        setError(t("confirmation.receipt_retry_detail", "L’action a réussi. Le reçu est indisponible : réessayez son enregistrement sans répéter l’action."));
        return;
      }
      if (createdReceipt) {
        const toastReceipt = { ...createdReceipt, session_id: pending.sessionId };
        setReceipt(toastReceipt);
        recordConfirmationEvent(eventPayload(pending, "confirmed", { receipt_id: createdReceipt.public_id, authentication_method: authenticationMethod }));
        const rollback = typeof result?.undo === "function" ? result.undo : configuration.undoAction;
        if (undoSeconds && typeof rollback === "function") {
          setUndoHandler(() => async () => {
            await rollback(result);
            const cancelled = await cancelConfirmationReceipt(createdReceipt.public_id, "user_undo");
            recordConfirmationEvent(eventPayload(pending, "undone", { receipt_id: createdReceipt.public_id }));
            setReceipt((current) => current ? { ...current, ...cancelled } : current);
            setUndoHandler(null);
          });
          recordConfirmationEvent(eventPayload(pending, "undo_offered", { receipt_id: createdReceipt.public_id }));
        }
      } else {
        recordConfirmationEvent(eventPayload(pending, "confirmed"));
      }
      if (result?.redirectUrl) window.location.assign(result.redirectUrl);
      completeOfflineConfirmation(pending.sessionId);
      setRecoverableDrafts(offlineConfirmationsForCurrentPath());
      setPending(null);
    } catch (actionError) {
      setError(apiErrorMessage(actionError, t));
      recordConfirmationEvent(eventPayload(pending, "failed", { reason: actionError?.response?.status ? `http_${actionError.response.status}` : "action_error" }));
    } finally {
      confirmingRef.current = false;
      setBusy(false);
    }
  }, [busy, eventPayload, lang, pending, t]);

  return (
    <PremiumActionConfirmationContext.Provider value={requestConfirmation}>
      {children}
      {pending ? (
        <PremiumParticipationConfirmation
          {...pending.configuration}
          experienceMode={pending.configuration.confirmationExperience || "premium"}
          managedLifecycle
          receiptPending={Boolean(pending.receiptPending)}
          busy={busy}
          error={error || pending.configuration?.error || ""}
          securityChallenge={pending.challenge}
          securityLoading={pending.securityLoading}
          securityError={pending.securityError}
          preferences={pending.configuration.confirmationExperience === "stable" ? { ...preferences, animation_intensity: "none" } : preferences}
          onConsentChange={consentChange}
          onSecurityMethod={(method) => pending && recordConfirmationEvent(eventPayload(pending, "security_method", { authentication_method: method, step_key: `security_${method}` }))}
          onClose={close}
          onConfirm={confirm}
        />
      ) : null}
      {receipt ? <ReceiptToast receipt={receipt} onClose={() => setReceipt(null)} onUndo={undoHandler} /> : null}
      {!pending && recoverableDrafts.length ? <aside className="premium-confirmation-recovery" role="status"><span aria-hidden="true">↻</span><div><strong><LocalizedText source={"Démarche sécurisée à reprendre"} /></strong><small><LocalizedText source={"Votre dernière confirmation a été interrompue. Relancez l’action sur cette page : la même clé sera réutilisée pour éviter tout doublon."} /></small></div><button type="button" onClick={() => { recoverableDrafts.forEach((item) => discardOfflineConfirmation(item.idempotency_key)); setRecoverableDrafts([]); }}><LocalizedText source={"Effacer"} /></button></aside> : null}
    </PremiumActionConfirmationContext.Provider>
  );
}

export function usePremiumActionConfirmation() {
  const context = useContext(PremiumActionConfirmationContext);
  if (!context) throw new Error("usePremiumActionConfirmation doit être utilisé dans PremiumActionConfirmationProvider.");
  return context;
}
